// @givmo/compliance — opening one vault document (CCM-WEB2).
//
// ══ THE LINK IS MINTED PER CLICK AND NEVER HELD ════════════════════════════════════════════════
//
// `GET …/artifacts/{id}/download` answers with a JSON payload carrying a short-lived signed URL —
// NOT a redirect, so the caller opens what it is handed rather than following the route. The backend
// mints it per request and persists it nowhere, and this component matches that: it fetches on
// click and keeps nothing. A URL cached in component state would outlive the authorization that
// produced it and turn a charity-scoped read into a bearer token for a jurisdiction's letter.
//
// ⚠ WHY THIS NAVIGATES RATHER THAN OPENING A TAB. The bytes were stored with
// `Content-Disposition: attachment` baked into the upload signature, so the signed GET serves them
// as a download: assigning the URL starts that download without taking the console anywhere. A
// `window.open` after an `await` is the popup-blocked path, and it is not needed here.
//
// ⚠ That behaviour DEPENDS on the disposition riding every upload — see
// the host adapter's `putArtifactBytes`, which sends the header the signature requires. If a
// document ever reached this bucket without it, this control would navigate away from the console
// instead of downloading.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Icon, testIdProp, toast } from '@givmo/design-system';
import { Download } from 'lucide-react';
import {
  decodeArtifactDownload,
  isVaultStorageUnavailable,
  serverMessage,
} from './compliance-decode';
import { useCompliance } from './compliance-context';
import { VAULT_UNAVAILABLE_COPY } from './artifact-upload';

/** Mint one document's signed link, per request, and hand it back (never held). */
async function mintDownload(
  operations: ReturnType<typeof useCompliance>['operations'],
  orgRef: string | null,
  artifactId: string,
): Promise<string> {
  if (orgRef === null) throw new Error('We couldn’t open that document. Please try again.');
  const resolved = decodeArtifactDownload(await operations.getArtifactDownload(orgRef, artifactId));
  if (resolved === undefined) {
    throw new Error('We couldn’t open that document. Please try again.');
  }
  return resolved.downloadUrl;
}

/** What a refused download says, in the vault's words where it has them. */
function downloadRefusal(error: unknown): string {
  return isVaultStorageUnavailable(error)
    ? VAULT_UNAVAILABLE_COPY
    : serverMessage(error, 'We couldn’t open that document. Please try again.');
}

/**
 * The same per-click download, for a control with nowhere beside it to say a refusal (a menu item, a
 * document's name on a filing's panel): the refusal is a toast. Downloaded, never previewed.
 */
export function useArtifactDownload(artifactId: string) {
  const { orgRef, operations } = useCompliance();
  return useMutation({
    mutationFn: () => mintDownload(operations, orgRef, artifactId),
    // Not held: the link is spent here and re-minted on the next click.
    onSuccess: (url) => window.location.assign(url),
    onError: (error: unknown) => toast.error(downloadRefusal(error)),
  });
}

export interface ArtifactDownloadLinkProps {
  artifactId: string;
  /** What the control says. The surrounding surface supplies the document's own name. */
  label: string;
  testId: string;
  /** `link` inside a sentence or a table cell; `secondary` where it stands on its own. */
  tone?: 'link' | 'secondary';
}

/** Fetch a fresh signed link for one document and start the download. */
export function ArtifactDownloadLink({
  artifactId,
  label,
  testId,
  tone = 'link',
}: ArtifactDownloadLinkProps) {
  const { orgRef, operations } = useCompliance();
  const [refusal, setRefusal] = useState<string | null>(null);

  const open = useMutation({
    mutationFn: () => mintDownload(operations, orgRef, artifactId),
    onSuccess: (url) => {
      setRefusal(null);
      // Not held in state: the link is spent here and re-minted on the next click.
      window.location.assign(url);
    },
    onError: (error: unknown) => setRefusal(downloadRefusal(error)),
  });

  return (
    <span className="inline-flex flex-col gap-2">
      <Button
        appearance="saas"
        tone={tone}
        size="sm"
        loading={open.isPending}
        leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
        onClick={() => open.mutate()}
        testId={testId}
      >
        {label}
      </Button>
      {refusal !== null && (
        <span className="text-s12 text-neutral-700" {...testIdProp(`${testId}-refusal`)}>
          {refusal}
        </span>
      )}
    </span>
  );
}
